import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import "./NewChangesModal.css";

export function NewChangesDialog({
	isOpen,
	update,
	onClose,
	confirmText = "Got it",
}) {
	useEffect(() => {
		if (!isOpen) return undefined;

		const handleKeyDown = (e) => {
			if (e.key === "Escape" || e.key === "Enter") {
				onClose();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen, onClose]);

	if (!isOpen || !update) return null;

	const title = update.title || "New changes";
	const items = Array.isArray(update.items) ? update.items : [];

	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return createPortal(
		<div
			className="modal-overlay new-changes-overlay"
			onClick={handleBackdropClick}
		>
			<div
				className="new-changes-modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby="new-changes-title"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="new-changes-header">
					<p className="new-changes-eyebrow">What's new</p>
					<h3 id="new-changes-title">{title}</h3>
					<button
						type="button"
						className="modal-close"
						onClick={onClose}
						aria-label="Close"
					>
						×
					</button>
				</div>
				<div className="new-changes-body">
					{update.summary ? <p>{update.summary}</p> : null}
					{items.length > 0 ? (
						<ul className="new-changes-list">
							{items.map((item) => (
								<li key={item}>{item}</li>
							))}
						</ul>
					) : null}
				</div>
				<div className="new-changes-actions">
					<button
						type="button"
						className="btn btn-primary"
						onClick={onClose}
						autoFocus
					>
						{confirmText}
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
}

export function NewChangesModalHost({ enabled }) {
	const [update, setUpdate] = useState(null);

	useEffect(() => {
		if (!enabled) {
			setUpdate(null);
			return undefined;
		}

		let cancelled = false;
		axios
			.get("/api/site-updates/pending")
			.then((response) => {
				if (!cancelled) {
					setUpdate(response.data?.update || null);
				}
			})
			.catch(() => {
				if (!cancelled) setUpdate(null);
			});

		return () => {
			cancelled = true;
		};
	}, [enabled]);

	const dismiss = useCallback(() => {
		setUpdate(null);
		axios.post("/api/site-updates/ack").catch(() => {});
	}, []);

	return (
		<NewChangesDialog isOpen={Boolean(update)} update={update} onClose={dismiss} />
	);
}

export default NewChangesModalHost;
